import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { clientContractsService } from '@/features/client-contracts/services/client-contracts.service';
import type {
  ClientContract,
  CreateClientContractDto,
  UpdateClientContractDto,
} from '@/features/client-contracts/services/client-contracts.service';
import { ContractDashboardCards } from '@/features/client-contracts/components/ContractDashboardCards';
import { ClientContractList } from '@/features/client-contracts/components/ClientContractList';
import { ClientContractForm } from '@/features/client-contracts/components/ClientContractForm';
import { clientsService } from '@/features/clients/services/clients.service';
import { getClientDisplayName } from '@/features/clients/utils/display';
import { useToast } from '@/hooks/use-toast';
import { History, Plus, Settings } from 'lucide-react';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/_authenticated/client-contracts/')({
  validateSearch: (search: Record<string, unknown>): { clientId?: string } => ({
    clientId: (search.clientId as string) || undefined,
  }),
  component: ClientContractsPage,
});

function isAttentionContract(contract: ClientContract) {
  const today = new Date();
  const todayAtMidnight = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const endDate = new Date(contract.endDate);
  const isExpired = endDate < todayAtMidnight;
  if (isExpired) return true;

  if (!contract.nextAdjustmentDate) return false;
  const nextAdjustmentDate = new Date(contract.nextAdjustmentDate);
  const nextAdjustmentAtMidnight = new Date(
    nextAdjustmentDate.getFullYear(),
    nextAdjustmentDate.getMonth(),
    nextAdjustmentDate.getDate(),
  );
  const daysToAdjustment = Math.ceil(
    (nextAdjustmentAtMidnight.getTime() - todayAtMidnight.getTime()) /
      (1000 * 60 * 60 * 24),
  );

  return daysToAdjustment >= 0 && daysToAdjustment <= 30;
}

function ClientContractsPage() {
  const navigate = useNavigate();
  const { clientId: clientIdFromUrl } = useSearch({
    from: '/_authenticated/client-contracts/',
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [preselectedClientId, setPreselectedClientId] = useState<
    string | undefined
  >(undefined);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<ClientContract | null>(
    null,
  );
  const [adjustedAt, setAdjustedAt] = useState('');
  const [nextAdjustmentDate, setNextAdjustmentDate] = useState('');
  const [newMonthlyValue, setNewMonthlyValue] = useState('');
  const [clientFilter, setClientFilter] = useState<string>('all');
  const [clientStatusFilter, setClientStatusFilter] = useState<
    'active' | 'inactive' | 'all'
  >('active');

  const formatInputDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
      date.getDate(),
    ).padStart(2, '0')}`;

  useEffect(() => {
    if (clientIdFromUrl) {
      setPreselectedClientId(clientIdFromUrl);
      setClientFilter(clientIdFromUrl);
      setCreateOpen(true);
    }
  }, [clientIdFromUrl]);

  const clientsListFilters = useMemo(() => {
    if (clientStatusFilter === 'active') return { isActive: true as const };
    if (clientStatusFilter === 'inactive') return { isActive: false as const };
    return undefined;
  }, [clientStatusFilter]);

  const { data: clients = [] } = useQuery({
    queryKey: ['clients-list', clientStatusFilter],
    queryFn: () => clientsService.listClients(clientsListFilters),
  });

  const { data: dashboard } = useQuery({
    queryKey: ['client-contracts-dashboard'],
    queryFn: () => clientContractsService.getDashboard(),
  });

  const { data: contractsList = [], isLoading: listLoading } = useQuery({
    queryKey: ['client-contracts-list', clientFilter, clientStatusFilter],
    queryFn: () =>
      clientContractsService.list({
        ...(clientFilter === 'all' ? {} : { clientId: clientFilter }),
        clientStatus: clientStatusFilter,
      }),
  });

  // Atenção sempre considera apenas clientes ativos
  const { data: activeContractsForAttention = [] } = useQuery({
    queryKey: ['client-contracts-list', 'attention-active'],
    queryFn: () => clientContractsService.list({ clientStatus: 'active' }),
  });

  const createMutation = useMutation({
    mutationFn: (payload: {
      data: CreateClientContractDto;
      file: File;
    }) => clientContractsService.create(payload.data, payload.file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-contracts-list'] });
      queryClient.invalidateQueries({ queryKey: ['client-contracts-dashboard'] });
      toast({ title: 'Contrato criado com sucesso' });
      setCreateOpen(false);
      setPreselectedClientId(undefined);
      if (clientIdFromUrl) {
        navigate({ to: '/client-contracts', search: { clientId: undefined } });
      }
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateClientContractDto;
    }) => clientContractsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-contracts-list'] });
      queryClient.invalidateQueries({ queryKey: ['client-contracts-dashboard'] });
      toast({ title: 'Contrato atualizado com sucesso' });
      setEditOpen(false);
      setSelectedContract(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => clientContractsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-contracts-list'] });
      queryClient.invalidateQueries({ queryKey: ['client-contracts-dashboard'] });
      toast({ title: 'Contrato removido' });
      setDeleteOpen(false);
      setSelectedContract(null);
    },
  });

  const adjustMutation = useMutation({
    mutationFn: (payload: {
      id: string;
      adjustedAt: string;
      nextAdjustmentDate: string;
      monthlyValue: number;
    }) =>
      clientContractsService.update(payload.id, {
        adjustedAt: payload.adjustedAt,
        nextAdjustmentDate: payload.nextAdjustmentDate,
        monthlyValue: payload.monthlyValue,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['client-contracts-list'] });
      queryClient.invalidateQueries({ queryKey: ['client-contracts-dashboard'] });
      toast({ title: 'Reajuste salvo com sucesso' });
      setAdjustOpen(false);
      setSelectedContract(null);
    },
  });

  const handleDownload = async (contract: ClientContract) => {
    try {
      const { url } = await clientContractsService.getDownloadUrl(contract.id);
      window.open(url, '_blank');
    } catch {
      // o interceptor do axios já mostra o toast de erro
    }
  };

  const openAdjustModal = (contract: ClientContract) => {
    const today = new Date();
    const oneYearLater = new Date(today);
    oneYearLater.setFullYear(oneYearLater.getFullYear() + 1);

    setSelectedContract(contract);
    setAdjustedAt(formatInputDate(today));
    setNextAdjustmentDate(formatInputDate(oneYearLater));
    setNewMonthlyValue(contract.monthlyValue ?? '');
    setAdjustOpen(true);
  };

  const handleAdjustSubmit = () => {
    if (!selectedContract || !adjustedAt || !nextAdjustmentDate || !newMonthlyValue) {
      toast({
        title: 'Preencha todos os campos do reajuste',
        variant: 'destructive',
      });
      return;
    }

    const parsedValue = Number(String(newMonthlyValue).replace(',', '.'));
    if (Number.isNaN(parsedValue) || parsedValue < 0) {
      toast({
        title: 'Informe um valor de contrato válido',
        variant: 'destructive',
      });
      return;
    }

    adjustMutation.mutate({
      id: selectedContract.id,
      adjustedAt,
      nextAdjustmentDate,
      monthlyValue: parsedValue,
    });
  };

  const sortedContracts = useMemo(() => {
    return [...contractsList].sort((a, b) =>
      getClientDisplayName(a.client).localeCompare(getClientDisplayName(b.client), 'pt-BR', {
        sensitivity: 'base',
      }),
    );
  }, [contractsList]);

  const attentionContracts = useMemo(() => {
    return [...activeContractsForAttention]
      .filter(({ contract }) => isAttentionContract(contract))
      .sort((a, b) =>
        getClientDisplayName(a.client).localeCompare(
          getClientDisplayName(b.client),
          'pt-BR',
          { sensitivity: 'base' },
        ),
      );
  }, [activeContractsForAttention]);

  const regularContracts = useMemo(() => {
    const attentionIds = new Set(attentionContracts.map((item) => item.contract.id));
    return sortedContracts.filter((item) => !attentionIds.has(item.contract.id));
  }, [attentionContracts, sortedContracts]);

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Contratos de Clientes</h1>
          <p className="text-muted-foreground">
            Gerencie contratos e aditivos dos clientes.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <Link to="/client-contracts/settings">
              <Settings className="h-4 w-4 mr-2" />
              Configurações
            </Link>
          </Button>
          <Button variant="outline" size="sm" asChild>
            <Link to="/client-contracts/history">
              <History className="h-4 w-4 mr-2" />
              Histórico
            </Link>
          </Button>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Contrato
          </Button>
        </div>
      </div>

      {dashboard && <ContractDashboardCards stats={dashboard} />}

      {attentionContracts.length > 0 && (
        <Card className="border-destructive/40">
          <CardHeader>
            <CardTitle className="text-destructive">
              Contratos que precisam de atenção
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              Esta lista mostra contratos vencidos ou com reajuste previsto para os próximos 30 dias.
            </p>
          </CardHeader>
          <CardContent>
            <ClientContractList
              items={attentionContracts}
              isLoading={listLoading}
              onSelect={(id) =>
                navigate({
                  to: `/clients/${attentionContracts.find((i) => i.contract.id === id)?.client.id}`,
                })
              }
              onEdit={(contract) => {
                setSelectedContract(contract);
                setEditOpen(true);
              }}
              onDelete={(contract) => {
                setSelectedContract(contract);
                setDeleteOpen(true);
              }}
              onDownload={handleDownload}
              onAdjust={openAdjustModal}
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center gap-4 flex-wrap">
            <CardTitle>Lista de Contratos</CardTitle>
            <div className="flex-1 min-w-[200px] max-w-xs">
              <Select
                value={clientStatusFilter}
                onValueChange={(value) => {
                  setClientStatusFilter(value as 'active' | 'inactive' | 'all');
                  setClientFilter('all');
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Status do cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Clientes ativos</SelectItem>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="inactive">Só inativos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 min-w-[200px] max-w-xs">
              <Select
                value={clientFilter}
                onValueChange={setClientFilter}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Filtrar por cliente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os clientes</SelectItem>
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {getClientDisplayName(c)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <ClientContractList
            items={regularContracts}
            isLoading={listLoading}
            onSelect={(id) =>
              navigate({
                to: `/clients/${regularContracts.find((i) => i.contract.id === id)?.client.id}`,
              })
            }
            onEdit={(contract) => {
              setSelectedContract(contract);
              setEditOpen(true);
            }}
            onDelete={(contract) => {
              setSelectedContract(contract);
              setDeleteOpen(true);
            }}
            onDownload={handleDownload}
            onAdjust={openAdjustModal}
          />
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open);
          if (!open) setPreselectedClientId(undefined);
        }}
      >
        <DialogContent size="lg" busy={createMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Criar contrato</DialogTitle>
          </DialogHeader>
          <ClientContractForm
            key={preselectedClientId ?? 'create'}
            mode="create"
            preselectedClientId={preselectedClientId}
            onSubmit={(data, file) => {
              if (!file) return;
              createMutation.mutate({
                data: data as CreateClientContractDto,
                file,
              });
            }}
            onCancel={() => setCreateOpen(false)}
            isLoading={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent size="lg" busy={updateMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Editar contrato</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <ClientContractForm
              mode="edit"
              initialData={selectedContract}
              onSubmit={(data) => {
                updateMutation.mutate({
                  id: selectedContract.id,
                  data: data as UpdateClientContractDto,
                });
              }}
              onCancel={() => {
                setEditOpen(false);
                setSelectedContract(null);
              }}
              isLoading={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent size="sm" busy={deleteMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Excluir contrato?</DialogTitle>
            <p className="text-sm text-muted-foreground">
              Esta ação não pode ser desfeita.
            </p>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() =>
                selectedContract &&
                deleteMutation.mutate(selectedContract.id)
              }
              loading={deleteMutation.isPending}
            >
              Excluir contrato
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={adjustOpen}
        onOpenChange={(open) => {
          setAdjustOpen(open);
          if (!open) {
            setSelectedContract(null);
          }
        }}
      >
        <DialogContent size="md" busy={adjustMutation.isPending}>
          <DialogHeader>
            <DialogTitle>Reajustar contrato</DialogTitle>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              handleAdjustSubmit();
            }}
          >
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="adjustedAt">Reajustado em</Label>
              <Input
                id="adjustedAt"
                type="date"
                value={adjustedAt}
                onChange={(e) => setAdjustedAt(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nextAdjustmentDate">Próximo reajuste</Label>
              <Input
                id="nextAdjustmentDate"
                type="date"
                value={nextAdjustmentDate}
                onChange={(e) => setNextAdjustmentDate(e.target.value)}
              />
            </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="newMonthlyValue">Novo valor do contrato</Label>
              <Input
                id="newMonthlyValue"
                type="number"
                min="0"
                step="0.01"
                value={newMonthlyValue}
                onChange={(e) => setNewMonthlyValue(e.target.value)}
                placeholder="0,00"
              />
            </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setAdjustOpen(false);
                setSelectedContract(null);
              }}
            >
              Cancelar
            </Button>
            <Button type="submit" loading={adjustMutation.isPending}>
              Salvar reajuste
            </Button>
          </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
